import React, { useState, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Modal, Button } from 'semantic-ui-react';
import theme from '../theme';
import {
    hasRole, isManagerRole, getScoreColor, getScoreDisplay,
    mapUserToTeamMember, MANAGER_ROLES, EXECUTIVE_ROLES
} from '../../pages/dashboardHelpers';
import Badge from '../components/Badge';
import { getTrainingPlanStatusForUser } from '../TrainingPlan/planState';
import GoalSettings from '../../components/Dashboard/GoalSettings';
import NewAssessmentDetails from '../../components/PerformanceReviews/NewAssessmentDetails';
import TeamActivityCharts from '../../components/TeamDashboard/TeamActivityCharts';
import {
    PageWrapper, PageTitle, SettingsButton,
    FilterBar, FilterGroup, FilterLabel, FilterSelect, FilterCount,
    ViewToggle, ToggleButton,
    ManagerGroupHeader, MemberGrid, MemberCard,
    CardHeaderRow, Avatar, MemberInfo, MemberName, MemberRole,
    StatsGrid, StatItem, StatNumber, StatLabel, CardFooter,
    DataTable, TableHead, TableRow, TableAvatar, TableAvatarCircle, ScoreText, SortIndicator,
    DetailPanel, DetailHeader, DetailTitle, CloseButton,
    EmptyState, EmptyIcon, EmptyTitle, EmptyText,
} from './TeamDashboardPage.styles';

function getInitials(name) {
    return name.split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

const GRADE_TO_BADGE = { '#28a745': 'success', '#ffc107': 'warning', '#dc3545': 'danger' };

function getStatusBadge(daysSince, cardColor) {
    let label;
    if (daysSince === null || daysSince === undefined) label = 'Never Practiced';
    else if (daysSince === 0) label = 'Practiced Today';
    else if (daysSince === 1) label = 'Yesterday';
    else label = `${daysSince}d ago`;

    const variant = GRADE_TO_BADGE[cardColor] || 'neutral';
    return { variant, label };
}

const TeamDashboardPage = observer(({ usersStore, companyStore, assessmentsStore }) => {
    const [teamSortBy, setTeamSortBy] = useState('name');
    const [teamFilterDept, setTeamFilterDept] = useState('all');
    const [teamFilterRole, setTeamFilterRole] = useState('all');
    const [expandedTeamMember, setExpandedTeamMember] = useState(null);
    const [viewMode, setViewMode] = useState('cards');
    const [goalSettingsOpen, setGoalSettingsOpen] = useState(false);
    // Training Plan assignment ("redo training") modal state. assignBump forces
    // the members memo to recompute after an in-place instance patch.
    const [assignTarget, setAssignTarget] = useState(null);
    const [assignBusy, setAssignBusy] = useState(false);
    const [assignError, setAssignError] = useState('');
    const [assignBump, setAssignBump] = useState(0);

    const currentUser = usersStore?.currentUser;
    const isStoreLoading = usersStore?.isLoading || assessmentsStore?.isLoading;
    const isDataReady = usersStore && currentUser && currentUser.userId;

    const isManager = hasRole(currentUser, MANAGER_ROLES);
    const isExecutive = hasRole(currentUser, EXECUTIVE_ROLES);
    const canViewTeam = isManager || isExecutive;

    const isSeniorManager = currentUser?.userType === 'Senior Manager';

    const allTeamMembers = useMemo(() => {
        if (!canViewTeam || !usersStore?.companyUsers || !currentUser) return [];
        // planStatus lets managers see where each report is in the guided
        // onboarding (Day N of 10 / complete / hidden); null hides the line
        // for members the plan doesn't apply to (veterans, staff).
        const toMember = (user) => ({
            ...mapUserToTeamMember(user, assessmentsStore, usersStore),
            planStatus: getTrainingPlanStatusForUser({ user, company: companyStore?.company, assessmentsStore }),
        });
        try {
            if (isExecutive) {
                const currentUserType = currentUser?.userType;
                return usersStore.companyUsers
                    .filter(user => {
                        if (!user?.userId || user.userId === currentUser.userId) return false;

                        if (currentUserType === 'admin') {
                            return user.userType !== 'admin';
                        }

                        return user.userType !== 'Company Admin' && user.userType !== 'admin';
                    })
                    .map(toMember);
            } else if (isSeniorManager && usersStore.getSeniorManagerVisibleUsers) {
                return usersStore.getSeniorManagerVisibleUsers(currentUser.userId)
                    .map(toMember);
            } else if (isManager && usersStore.getDirectReports) {
                return usersStore.getDirectReports(currentUser.userId)
                    .map(toMember);
            }
        } catch (error) {
            console.error('Error in getTeamMembers:', error);
        }
        return [];
    }, [canViewTeam, isExecutive, isManager, isSeniorManager, usersStore?.companyUsers, currentUser?.userId, assessmentsStore, companyStore?.company, assignBump]);

    // Who may assign: exactly the roles that can view this page (managers +
    // executives, read-only variants included — assigning is coaching, not
    // member management). Server enforces the same matrix (managers
    // additionally tree-scoped) in users/patch.js.
    const canAssignPlan = canViewTeam;

    const resolveLiveUser = (memberId) => usersStore?.companyUsers?.find((u) => u.userId === memberId);
    const assignTargetLive = assignTarget ? resolveLiveUser(assignTarget.id) : null;

    const runAssignAction = async (action) => {
        if (!assignTargetLive || typeof assignTargetLive[action] !== 'function') {
            setAssignError('Could not find that user. Refresh the page and try again.');
            return;
        }
        setAssignBusy(true);
        setAssignError('');
        try {
            await assignTargetLive[action]();
            setAssignTarget(null);
            setAssignBump((n) => n + 1);
        } catch (err) {
            console.error(`Training plan ${action} failed:`, err);
            setAssignError('That didn\'t save. Please try again.');
        } finally {
            setAssignBusy(false);
        }
    };

    const assignButtonLabel = (member) =>
        (!member.planStatus || member.planStatus.kind === 'notStarted') ? 'Assign Plan' : 'Restart Plan';

    const roles = useMemo(() => [...new Set(allTeamMembers.map(m => m.role))].sort(), [allTeamMembers]);
    const managers = useMemo(() => {
        if (!isExecutive || !usersStore?.companyUsers) return [];
        return usersStore.companyUsers
            .filter(u => isManagerRole(u.userType) || u.userType === 'Company Admin' || u.userType === 'Executive' || u.userType === 'admin')
            .map(m => ({ id: m.userId, name: `${m.firstName} ${m.lastName}` }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [isExecutive, usersStore?.companyUsers]);

    const managerNameById = useMemo(() => {
        const map = {};
        (usersStore?.companyUsers || []).forEach(u => { map[u.userId] = `${u.firstName} ${u.lastName}`; });
        return map;
    }, [usersStore?.companyUsers]);

    const filteredAndSortedMembers = useMemo(() => {
        return allTeamMembers
            .filter(member => {
                const roleMatch = teamFilterRole === 'all' || member.role === teamFilterRole;
                const managerMatch = teamFilterDept === 'all' || member.reportsToId === teamFilterDept || member.id === teamFilterDept;
                return roleMatch && managerMatch;
            })
            .sort((a, b) => {
                switch (teamSortBy) {
                    case 'name': return a.name.localeCompare(b.name);
                    case 'role': return a.role.localeCompare(b.role);
                    case 'manager':
                        return (managerNameById[a.reportsToId] || 'No Manager').localeCompare(managerNameById[b.reportsToId] || 'No Manager');
                    case 'avgScore': return (b.avgScore || 0) - (a.avgScore || 0);
                    case 'totalPractices': return b.totalPractices - a.totalPractices;
                    case 'recentActivity': return (a.daysSinceLastPractice ?? Infinity) - (b.daysSinceLastPractice ?? Infinity);
                    default: return 0;
                }
            });
    }, [allTeamMembers, teamFilterRole, teamFilterDept, teamSortBy, managerNameById]);

    const membersByManager = useMemo(() => {
        if (!isExecutive) return { 'My Team': filteredAndSortedMembers };
        const managerIds = new Set(managers.map(m => m.id));
        const grouped = managers.reduce((acc, manager) => {
            const reports = filteredAndSortedMembers.filter(m => m.reportsToId === manager.id);
            if (reports.length > 0) {
                const managerCard = filteredAndSortedMembers.find(m => m.id === manager.id);
                acc[manager.name] = managerCard ? [managerCard, ...reports] : reports;
            }
            return acc;
        }, {});
        const assigned = new Set(Object.values(grouped).flat().map(m => m.id));
        const unassigned = filteredAndSortedMembers.filter(m => !assigned.has(m.id));
        if (unassigned.length > 0) {
            grouped['Unassigned'] = unassigned;
        }
        return grouped;
    }, [isExecutive, managers, filteredAndSortedMembers]);

    // Loading state
    if (isStoreLoading || !isDataReady) {
        return (
            <PageWrapper>
                <EmptyState>
                    <EmptyTitle>{isStoreLoading ? 'Loading...' : 'Loading user data...'}</EmptyTitle>
                </EmptyState>
            </PageWrapper>
        );
    }

    // Access denied
    if (!canViewTeam) {
        return (
            <PageWrapper>
                <PageTitle>Team Dashboard</PageTitle>
                <EmptyState>
                    <EmptyIcon>{'\uD83D\uDD12'}</EmptyIcon>
                    <EmptyTitle>Access Restricted</EmptyTitle>
                    <EmptyText>The Team Dashboard is available to managers, senior managers, executives, and admins.</EmptyText>
                </EmptyState>
            </PageWrapper>
        );
    }

    // No team members
    if (allTeamMembers.length === 0) {
        return (
            <PageWrapper>
                <PageTitle>Team Dashboard</PageTitle>
                <EmptyState>
                    <EmptyIcon>{'\uD83D\uDC65'}</EmptyIcon>
                    <EmptyTitle>No Team Members Found</EmptyTitle>
                    <EmptyText>There are no team members to display at this time.</EmptyText>
                </EmptyState>
            </PageWrapper>
        );
    }

    return (
        <PageWrapper>
            <PageTitle>
                {isExecutive ? 'Company Overview' : 'My Team'}
                {canViewTeam && !currentUser?.isSalesMember && (
                    <SettingsButton
                        title="Practice Goal Settings"
                        onClick={() => setGoalSettingsOpen(true)}
                    >
                        {'\u2699'}
                    </SettingsButton>
                )}
            </PageTitle>

            {/* Filters — executives only */}
            {isExecutive && (
                <FilterBar>
                    <FilterGroup>
                        <FilterLabel>Sort by:</FilterLabel>
                        <FilterSelect value={teamSortBy} onChange={(e) => setTeamSortBy(e.target.value)}>
                            <option value="name">Name</option>
                            <option value="manager">Manager</option>
                            <option value="role">Role</option>
                            <option value="avgScore">Avg Score</option>
                            <option value="totalPractices">Total Practices</option>
                            <option value="recentActivity">Recent Activity</option>
                        </FilterSelect>
                    </FilterGroup>

                    <FilterGroup>
                        <FilterLabel>Manager:</FilterLabel>
                        <FilterSelect value={teamFilterDept} onChange={(e) => setTeamFilterDept(e.target.value)}>
                            <option value="all">All Managers</option>
                            {managers.map(manager => (
                                <option key={manager.id} value={manager.id}>{manager.name}</option>
                            ))}
                        </FilterSelect>
                    </FilterGroup>

                    <FilterGroup>
                        <FilterLabel>Role:</FilterLabel>
                        <FilterSelect value={teamFilterRole} onChange={(e) => setTeamFilterRole(e.target.value)}>
                            <option value="all">All Roles</option>
                            {roles.map(role => (
                                <option key={role} value={role}>{role}</option>
                            ))}
                        </FilterSelect>
                    </FilterGroup>

                    <FilterCount>
                        Showing {filteredAndSortedMembers.length} of {allTeamMembers.length} members
                    </FilterCount>
                </FilterBar>
            )}

            {/* Team Activity Charts */}
            <TeamActivityCharts teamMembers={filteredAndSortedMembers} assessmentsStore={assessmentsStore} companyId={companyStore?.company?.id} investorPitchEnabled={companyStore?.company?.investorPitchEnabled} />

            {/* View Toggle */}
            <ViewToggle>
                <ToggleButton $active={viewMode === 'cards'} onClick={() => setViewMode('cards')}>
                    Cards
                </ToggleButton>
                <ToggleButton $active={viewMode === 'table'} onClick={() => setViewMode('table')}>
                    Table
                </ToggleButton>
            </ViewToggle>

            {/* Table View */}
            {viewMode === 'table' && (
                <DataTable>
                    <TableHead>
                        <tr>
                            <th onClick={() => setTeamSortBy('name')}>
                                Name <SortIndicator $active={teamSortBy === 'name'}>{'\u25B2'}</SortIndicator>
                            </th>
                            <th onClick={() => setTeamSortBy('role')}>
                                Role <SortIndicator $active={teamSortBy === 'role'}>{'\u25B2'}</SortIndicator>
                            </th>
                            {isExecutive && (
                                <th onClick={() => setTeamSortBy('manager')}>
                                    Manager <SortIndicator $active={teamSortBy === 'manager'}>{'\u25B2'}</SortIndicator>
                                </th>
                            )}
                            <th onClick={() => setTeamSortBy('avgScore')}>
                                Avg Score <SortIndicator $active={teamSortBy === 'avgScore'}>{'\u25BC'}</SortIndicator>
                            </th>
                            <th onClick={() => setTeamSortBy('totalPractices')}>
                                Total <SortIndicator $active={teamSortBy === 'totalPractices'}>{'\u25BC'}</SortIndicator>
                            </th>
                            <th>Training Plan</th>
                            <th>Last 30d</th>
                            <th onClick={() => setTeamSortBy('recentActivity')}>
                                Days Since <SortIndicator $active={teamSortBy === 'recentActivity'}>{'\u25B2'}</SortIndicator>
                            </th>
                        </tr>
                    </TableHead>
                    <tbody>
                        {filteredAndSortedMembers.map(member => (
                            <TableRow
                                key={member.id}
                                $bgColor={expandedTeamMember === member.id ? theme.colors.primaryGhost : undefined}
                                onClick={() => setExpandedTeamMember(expandedTeamMember === member.id ? null : member.id)}
                            >
                                <td>
                                    <TableAvatar>
                                        <TableAvatarCircle>{getInitials(member.name)}</TableAvatarCircle>
                                        <strong>{member.name}</strong>
                                    </TableAvatar>
                                </td>
                                <td>{member.role}</td>
                                {isExecutive && (
                                    <td>{managerNameById[member.reportsToId] || 'N/A'}</td>
                                )}
                                <td>
                                    <ScoreText $color={member.avgScore ? getScoreColor(member.avgScore) : null}>
                                        {member.avgScore ? getScoreDisplay(member.avgScore) : 'N/A'}
                                    </ScoreText>
                                </td>
                                <td>{member.totalPractices}</td>
                                <td>
                                    {member.planStatus?.label || 'N/A'}
                                    {canAssignPlan && (
                                        <Button
                                            size="mini"
                                            basic
                                            style={{ marginLeft: 8, fontFamily: 'Montserrat' }}
                                            onClick={(e) => { e.stopPropagation(); setAssignError(''); setAssignTarget(member); }}
                                        >
                                            {assignButtonLabel(member)}
                                        </Button>
                                    )}
                                </td>
                                <td>{member.practicesLast30Days}</td>
                                <td>
                                    {member.daysSinceLastPractice === null || member.daysSinceLastPractice === undefined
                                        ? 'N/A'
                                        : `${member.daysSinceLastPractice}d`}
                                </td>
                            </TableRow>
                        ))}
                    </tbody>
                </DataTable>
            )}

            {/* Card View */}
            {viewMode === 'cards' && Object.entries(membersByManager).map(([managerName, members]) => (
                members.length > 0 ? (
                    <div key={managerName}>
                        {isExecutive && Object.keys(membersByManager).length > 1 && (
                            <ManagerGroupHeader>
                                {managerName}'s Team ({members.length})
                            </ManagerGroupHeader>
                        )}

                        <MemberGrid>
                            {members.map((member) => {
                                const badge = getStatusBadge(member.daysSinceLastPractice, member.cardColor);

                                return (
                                    <MemberCard key={member.id}>
                                        <CardHeaderRow>
                                            <Avatar>{getInitials(member.name)}</Avatar>
                                            <MemberInfo>
                                                <MemberName>{member.name}</MemberName>
                                                <MemberRole>{member.role}</MemberRole>
                                                {member.planStatus && (
                                                    <MemberRole>Training Plan · {member.planStatus.label}</MemberRole>
                                                )}
                                                {canAssignPlan && (
                                                    <Button
                                                        size="mini"
                                                        basic
                                                        style={{ marginTop: 6, alignSelf: 'flex-start', fontFamily: 'Montserrat' }}
                                                        onClick={() => { setAssignError(''); setAssignTarget(member); }}
                                                    >
                                                        {assignButtonLabel(member)}
                                                    </Button>
                                                )}
                                            </MemberInfo>
                                            <Badge $variant={badge.variant}>{badge.label}</Badge>
                                        </CardHeaderRow>

                                        <StatsGrid>
                                            <StatItem>
                                                <StatNumber>{member.totalPractices}</StatNumber>
                                                <StatLabel>Total</StatLabel>
                                            </StatItem>
                                            <StatItem>
                                                <StatNumber>{member.practicesLast30Days}</StatNumber>
                                                <StatLabel>Last 30d</StatLabel>
                                            </StatItem>
                                            <StatItem>
                                                <StatNumber $color={member.avgScore ? getScoreColor(member.avgScore) : null}>
                                                    {member.avgScore ? getScoreDisplay(member.avgScore) : 'N/A'}
                                                </StatNumber>
                                                <StatLabel>Avg Score</StatLabel>
                                            </StatItem>
                                            <StatItem>
                                                <StatNumber>
                                                    {member.daysSinceLastPractice === null || member.daysSinceLastPractice === undefined
                                                        ? 'N/A'
                                                        : `${member.daysSinceLastPractice}d`}
                                                </StatNumber>
                                                <StatLabel>Days Since</StatLabel>
                                            </StatItem>
                                            {(member.teamSize !== undefined || isManagerRole(member.role)) && (
                                                <StatItem>
                                                    <StatNumber $color={theme.colors.primary}>
                                                        {member.teamSize || 0}
                                                    </StatNumber>
                                                    <StatLabel>
                                                        {member.teamSize === 1 ? 'Report' : 'Reports'}
                                                    </StatLabel>
                                                </StatItem>
                                            )}
                                        </StatsGrid>

                                        <CardFooter
                                            onClick={() => setExpandedTeamMember(
                                                expandedTeamMember === member.id ? null : member.id
                                            )}
                                        >
                                            {isManagerRole(member.role)
                                                ? (member.teamSize > 0 ? `View Team (${member.teamSize}) & Practices` : 'View Team & Practices')
                                                : 'View Recent Practices'
                                            }
                                        </CardFooter>
                                    </MemberCard>
                                );
                            })}
                        </MemberGrid>
                    </div>
                ) : null
            ))}

            {/* Expanded Member Details */}
            {expandedTeamMember && (() => {
                const selectedMember = allTeamMembers.find(m => m.id === expandedTeamMember);
                if (!selectedMember || !assessmentsStore) return null;

                let memberAssessments = [];
                try {
                    memberAssessments = assessmentsStore.closedAssessmentsForUser(selectedMember.id, companyStore?.company?.id) || [];
                } catch (e) {
                    console.error('Error loading assessments for', selectedMember.name, e);
                }

                return (
                    <DetailPanel
                        ref={el => { if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                    >
                        <DetailHeader>
                            <DetailTitle>{selectedMember.name} - Performance Reports</DetailTitle>
                            <CloseButton onClick={() => setExpandedTeamMember(null)}>
                                {'\u2715'}
                            </CloseButton>
                        </DetailHeader>

                        {memberAssessments.length === 0 ? (
                            <EmptyState>
                                <p><strong>{selectedMember.name}</strong> hasn't completed any practice sessions yet.</p>
                                <p>Encourage them to start practicing to see their feedback and progress here!</p>
                            </EmptyState>
                        ) : (
                            <NewAssessmentDetails assessments={[...memberAssessments].sort((a, b) =>
                                new Date(b.completedDateTime) - new Date(a.completedDateTime)
                            )} />
                        )}
                    </DetailPanel>
                );
            })()}

            <GoalSettings
                companyStore={companyStore}
                open={goalSettingsOpen}
                onClose={() => setGoalSettingsOpen(false)}
            />

            {/* Assign / restart the Training Plan for a team member */}
            <Modal open={!!assignTarget} onClose={() => !assignBusy && setAssignTarget(null)} size="mini" closeIcon>
                <Modal.Header style={{ fontFamily: 'Montserrat' }}>
                    {assignTarget && assignButtonLabel(assignTarget) === 'Assign Plan'
                        ? `Assign the Training Plan to ${assignTarget?.name}?`
                        : `Restart ${assignTarget?.name}'s Training Plan?`}
                </Modal.Header>
                <Modal.Content>
                    <p style={{ fontFamily: 'Montserrat', fontSize: 14, color: '#444' }}>
                        Their 10-day Training Plan starts fresh from today at Day 1. Practice history
                        stays intact, but progress on any previous plan run restarts. They'll see a
                        refresher welcome and the daily plan on their next visit.
                    </p>
                    {assignError && (
                        <p style={{ fontFamily: 'Montserrat', fontSize: 13, color: '#dc3545' }}>{assignError}</p>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    {assignTargetLive?.trainingPlanAssignedAt && (
                        <Button
                            basic
                            floated="left"
                            disabled={assignBusy}
                            onClick={() => runAssignAction('unassignTrainingPlan')}
                            style={{ fontFamily: 'Montserrat' }}
                        >
                            Remove from plan
                        </Button>
                    )}
                    <Button disabled={assignBusy} onClick={() => setAssignTarget(null)} style={{ fontFamily: 'Montserrat' }}>
                        Cancel
                    </Button>
                    <Button
                        primary
                        loading={assignBusy}
                        onClick={() => runAssignAction('assignTrainingPlan')}
                        style={{ fontFamily: 'Montserrat', backgroundColor: '#137fa2' }}
                    >
                        {assignTarget && assignButtonLabel(assignTarget) === 'Assign Plan' ? 'Start Plan' : 'Restart Plan'}
                    </Button>
                </Modal.Actions>
            </Modal>
        </PageWrapper>
    );
});

export default TeamDashboardPage;
